import { usePage } from '@inertiajs/vue3';

/**
 * Formate un nombre entier long selon la locale de l'application (props.locales.current)
 * Supporte les nombres, les BigInt et les chaînes numériques.
 * 
 * @param {number|BigInt|string} value - Le nombre à formater
 * @returns {string} - Le nombre formaté (ex: "1 234 567" pour FR, "1,234,567" pour EN)
 */
export const formatLongInt = (value) => {
    if (value === null || value === undefined || value === '') return '';

    // Conversion en nombre ou BigInt si c'est une chaîne
    let numValue = value;
    if (typeof value === 'string') {
        // Nettoyage de la chaîne si besoin (ex: supprimer les espaces)
        const cleanValue = value.trim();
        if (cleanValue === '') return '';

        try {
            // Tentative de conversion en BigInt si c'est un très long nombre, sinon Number
            numValue = cleanValue.length > 15 ? BigInt(cleanValue) : Number(cleanValue);
        } catch (e) {
            numValue = Number(cleanValue);
        }
    }

    if (isNaN(numValue) && typeof numValue !== 'bigint') return value;

    // Récupération de la locale depuis les props partagées d'Inertia
    // Par défaut 'fr' si non défini
    const locale = usePage().props.locales?.current || 'fr';

    // Mapping vers les identifiants de locale BCP 47 standards
    const localesMap = {
        'fr': 'fr-FR',
        'en': 'en-US'
    };

    const standardLocale = localesMap[locale] || locale;

    return new Intl.NumberFormat(standardLocale).format(numValue);
};

/**
 * Formate une longueur génomique (bp, Kb, Mb)
 * 
 * @param {number|string} length - La longueur en paires de bases
 * @returns {string} - La longueur formatée avec l'unité
 */
export const formatLength = (length) => {
    if (length === null || length === undefined || length === '') return '';

    const val = Number(length);
    if (isNaN(val)) return length;

    if (val < 1000) {
        return formatLongInt(val) + " bp";
    } else if (val < 1000000) {
        return (val / 1000).toFixed(2).replace('.', ',') + " Kb";
    } else {
        return (val / 1000000).toFixed(2).replace('.', ',') + " Mb";
    }
};

/**
 * Compact AF threshold for filter chips / summaries (not the slider tooltip).
 *
 * Values below 0.01 use scientific notation (`1.00e-4`) because a 6-decimal
 * string is unreadable in chips. The slider tooltip uses toFixed(6) instead
 * (Slider.vue formatGnomadAf) so the dragged value stays exact.
 *
 * @req REQ-AF-001 — exponential display below 0.01 (non-linear rare AF scale)
 * @param {number|string} value  Allele frequency in [0, 1].
 * @returns {string}
 */
export const formatAfThreshold = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) {
        return '-';
    }
    if (num === 0) {
        return '0';
    }
    if (num < 0.01) {
        return num.toExponential(2);
    }
    return String(num);
};

/**
 * Composable pour utiliser les formateurs dans les composants Vue 3
 */
export function useFormatter() {
    return {
        formatLongInt,
        formatLength,
        formatAfThreshold
    };
}
